import { useState, useRef, useCallback, useEffect, ReactNode } from "react";
import { TooltipProvider } from "@/components/ui/tooltip";
import {
  IconBrandGoogle,
  IconBrandLinkedin,
  IconBrandMeta,
  IconBrandTiktok,
} from "@tabler/icons-react";
import { cn } from "@/lib/utils";
import { useGlobalStore } from "@/GlobalStore";
import { PlatformType } from "@/types";
import { RiGitForkLine, RiSidebarFoldLine } from "@remixicon/react";

type SidebarWrapperProps = {
  children: ReactNode;
};

const SidebarWrapper = ({ children }: SidebarWrapperProps) => {
  const [isResizing, setIsResizing] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);

  // Use global store for width and collapsed state
  const {
    sidebarWidth,
    setSidebarWidth,
    sidebarCollapsed,
    setSidebarCollapsed,
    selectedAccount,
  } = useGlobalStore();

  const renderAccountPlatform = () => {
    let icon, name;
    switch (selectedAccount?.platform) {
      case PlatformType.FACEBOOK:
        icon = <IconBrandMeta size={16} className="text-brand-primary-400" />;
        name = "Meta";
        break;
      case PlatformType.TIKTOK:
        icon = <IconBrandTiktok size={16} className="text-brand-primary-400" />;
        name = "TikTok";
        break;
      case PlatformType.GOOGLE:
        icon = <IconBrandGoogle size={16} className="text-brand-primary-400" />;
        name = "Google";
        break;
      case PlatformType.LINKEDIN:
        icon = <IconBrandLinkedin size={16} className="text-brand-primary-400" />;
        name = "LinkedIn";
        break;
    }

    return (
      <div className="flex items-center gap-1">
        {icon}
        <span className="mt-[1px] text-xs font-normal text-brand-primary-400">
          {name}
        </span>
      </div>
    );
  };

  const MIN_WIDTH = 200;
  const MAX_WIDTH = 450;

  const toggleSidebar = useCallback(() => {
    setSidebarCollapsed(!sidebarCollapsed);
  }, [sidebarCollapsed, setSidebarCollapsed]);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (sidebarCollapsed) return; // Don't allow resizing when collapsed
      e.preventDefault();
      setIsResizing(true);
    },
    [sidebarCollapsed],
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isResizing || !sidebarRef.current || sidebarCollapsed) return;

      const sidebarRect = sidebarRef.current.getBoundingClientRect();
      const newWidth = e.clientX - sidebarRect.left;

      // Constrain width between min and max
      const constrainedWidth = Math.min(Math.max(newWidth, MIN_WIDTH), MAX_WIDTH);
      setSidebarWidth(constrainedWidth);
    },
    [isResizing, sidebarCollapsed, setSidebarWidth],
  );

  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
  }, []);

  useEffect(() => {
    return () => {
      setSidebarCollapsed(false);
    };
  }, [setSidebarCollapsed]);

  // Add global mouse move and mouse up listeners
  useEffect(() => {
    if (isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [isResizing, handleMouseMove, handleMouseUp]);

  // Collapsed state - simple icon only
  if (sidebarCollapsed) {
    return (
      <div className="h-[32px] w-[32px] absolute top-[8px] left-[8px] z-10 rounded-[8px] flex justify-center items-center gap-[6px] squircle squircle-radius-[8px] squircle-outline-color-[#E7E8E9] squircle-smooth-[1] squircle-outline-[1px] shadow-sm">
        <div
          onClick={toggleSidebar}
          className={cn(
            "cursor-pointer squircle squircle-mask squircle-radius-[4.5px] squircle-fill-[#F6F6F6] squircle-smooth-[1] transition-colors flex w-[24px] h-[24px] justify-center items-center shrink-0",
          )}
        >
          <RiSidebarFoldLine
            size={16}
            className="text-[#02185933] transition-transform duration-200 rotate-180 shrink-0"
          />
        </div>
      </div>
    );
  }

  const isMerged = selectedAccount?.type === "MERGED";

  // Expanded state - full sidebar
  return (
    <div
      ref={sidebarRef}
      className="relative h-full px-[6px] pt-[8px] pb-1 rounded-r-[12px] border border-l-0 border-r-border-gray bg-white"
      style={{ width: `${sidebarWidth}px`, backgroundColor: "white" }}
    >
      <div className="h-full flex flex-col gap-[16px]">
        <div className="flex px-[8px] pt-[6px] justify-between items-center">
          {isMerged ? (
            <div className="flex items-center gap-1">
              <RiGitForkLine size={16} className="text-brand-primary-400" />
              <span className="mt-[1px] text-xs font-normal text-brand-primary-400">
                Combined
              </span>
            </div>
          ) : (
            renderAccountPlatform()
          )}
          <div
            onClick={toggleSidebar}
            className="cursor-pointer hover:bg-[#F6F6F6] rounded transition-colors"
          >
            <RiSidebarFoldLine
              size={16}
              className="text-[#02185933] transition-transform duration-200 shrink-0"
            />
          </div>
        </div>
        <div className="flex-1 w-full overflow-hidden">
          <TooltipProvider>{children}</TooltipProvider>
        </div>
      </div>

      {/* Resize handle */}
      <div
        className="absolute top-0 right-0 w-1 h-full cursor-col-resize"
        onMouseDown={handleMouseDown}
      />
    </div>
  );
};

export default SidebarWrapper;
